/** A refresh attempt's outcome. A discriminated OBJECT, never a bare string
 *  union: a bare string is truthy, so an old `if (await refresh())` check would
 *  silently pass on a rejection. The required `.outcome` read is the point.
 *
 *  `offline` means the transport failed before the server could answer —
 *  callers may keep the session and retry later, so offline capture keeps its
 *  session instead of being signed out by a dead network. */
export type RefreshOutcome =
  | { outcome: "refreshed" }
  | { outcome: "rejected" }
  | { outcome: "offline" };

/** The two outcomes a failure callback can observe. */
export type RefreshFailureOutcome = "rejected" | "offline";

/** The token triple a persisted store publishes to sibling contexts.
 *  `expiresAt` is epoch milliseconds — the persisted representation — where the
 *  wire's `onTokenRefreshed` carries the server's ISO string. */
export interface PersistedTokenPair {
  accessToken: string;
  refreshToken: string;
  expiresAt: number | null;
  /** Epoch-ms stamp of the moment this pair was minted. The ONLY thing that
   *  orders a sibling's view of the session against this context's — two
   *  token strings say nothing about which came second. Zero on a pair
   *  persisted before stamping existed, which therefore loses to any stamped
   *  pair. */
  rotatedAt: number;
}

/** The Web Locks name every context of one deployment contends on. Shared by
 *  all four portals: they never run on the same origin, so one name is enough
 *  and a per-app name would only invite a typo that silently disables the
 *  mutual exclusion. */
export const REFRESH_LOCK_NAME = "njbsoft-token-refresh";

interface RefreshManagerOptions {
  baseUrl: string;
  getRefreshToken: () => string | null;
  onTokenRefreshed: (accessToken: string, refreshToken: string, expiresAt: string) => void;
  onRefreshFailed: (outcome: RefreshFailureOutcome) => void;
  /** Reads the PERSISTED token triple — the storage sibling contexts share
   *  (localStorage behind a Zustand persist key). Returns null when no complete
   *  persisted session exists. Optional: a single-context host has no sibling
   *  to read through to. */
  getPersistedTokens?: () => PersistedTokenPair | null;
  /** Adopts a sibling context's rotation into THIS context's in-memory store.
   *  Optional, but a host supplying `getPersistedTokens` must supply this too —
   *  the adopt short-circuit is skipped unless both are present. */
  adoptTokens?: (tokens: PersistedTokenPair) => void;
  /** This context's stamp for the pair `getRefreshToken` returns. Read together
   *  with `getPersistedTokens` inside the lock: persisted-newer means a sibling
   *  rotated while this context waited. Absent → 0, which makes every stamped
   *  persisted pair look newer, so a host that supplies `getPersistedTokens`
   *  must supply this too. */
  getTokenStamp?: () => number;
}

/**
 * Two serializations guard the refresh, one per sharing boundary. Within a JS
 * context, `inflightPromise` dedupes concurrent callers onto one attempt.
 * Across contexts sharing one persisted session (browser tabs), the Web Locks
 * API makes attempts mutually exclusive, and the read-through inside the lock
 * decides whether the wire is needed at all: a persisted pair STAMPED LATER
 * than the one captured before the lock means a sibling already rotated, so
 * this context ADOPTS the persisted pair and returns `refreshed` with zero
 * network traffic.
 *
 * That short-circuit is load-bearing, not an optimization — the server revokes
 * the whole rotation family when a rotated token is replayed past its grace
 * window, so a tab refreshing with a stale in-memory token signs every tab out.
 * The comparison is by STAMP and not by token inequality: an equality test
 * cannot tell a sibling's newer pair from this context's own newer pair, so it
 * reads a storage regression as agreement and lets the spent token onto the
 * wire. The regression itself is fenced in the persist layer
 * (`@njbsoft/store`'s guarded storage); this is the second wall.
 *
 * A host without Web Locks (React Native, a node test, a browser too old)
 * keeps the in-flight promise as its only serialization, which is correct
 * there: those hosts have no sibling context to contend with.
 */
class RefreshManager {
  private readonly options: RefreshManagerOptions;
  private inflightPromise: Promise<RefreshOutcome> | null = null;

  constructor(options: RefreshManagerOptions) {
    this.options = options;
  }

  get isRefreshing(): boolean {
    return this.inflightPromise !== null;
  }

  async refresh(): Promise<boolean> {
    return (await this.refreshOutcome()).outcome === "refreshed";
  }

  async refreshOutcome(): Promise<RefreshOutcome> {
    if (this.inflightPromise) {
      return this.inflightPromise;
    }

    this.inflightPromise = this.doRefresh();
    try {
      return await this.inflightPromise;
    } finally {
      this.inflightPromise = null;
    }
  }

  private async doRefresh(): Promise<RefreshOutcome> {
    // Captured BEFORE the lock: a sibling that rotates while this context waits
    // must still be visible as persisted-newer once the lock is granted — even
    // if a storage listener patched the in-memory store in the meantime.
    const captured = this.options.getRefreshToken();
    const capturedStamp = this.options.getTokenStamp?.() ?? 0;

    const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
    if (locks === undefined) {
      return this.refreshHoldingLock(captured, capturedStamp);
    }

    // The settled capture keeps the never-rejects contract airtight against a
    // lock that rejects (an aborted request, a browser tearing the page down):
    // a rejection after the callback settled returns the real outcome, one
    // before it classifies as transport failure.
    let settled: RefreshOutcome | null = null;
    try {
      // The DOM lib's generic infers the callback's promise as the lock value
      // (Promise<Promise<...>>); the await flattens it back to the outcome.
      return await locks.request(REFRESH_LOCK_NAME, { mode: "exclusive" }, async () => {
        settled = await this.refreshHoldingLock(captured, capturedStamp);
        return settled;
      });
    } catch {
      return settled ?? this.fail("offline");
    }
  }

  private async refreshHoldingLock(
    captured: string | null,
    capturedStamp: number,
  ): Promise<RefreshOutcome> {
    const { getPersistedTokens, adoptTokens } = this.options;
    if (getPersistedTokens && adoptTokens) {
      const persisted = getPersistedTokens();
      if (persisted !== null && persisted.rotatedAt > capturedStamp) {
        // A sibling already rotated: adopt its pair, keep the spent token off
        // the wire. Mirrors the onTokenRefreshed posture below — a throwing
        // app callback resolves as a failure rather than rejecting.
        try {
          adoptTokens(persisted);
          return { outcome: "refreshed" };
        } catch {
          return this.fail("rejected");
        }
      }
    }

    if (!captured) {
      // No token means re-login, not grace.
      return this.fail("rejected");
    }

    // The fetch sits in its own try/catch so only a transport failure — the one
    // case indistinguishable from a dead network — classifies as `offline`.
    let res: Response;
    try {
      res = await fetch(`${this.options.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ refresh_token: captured }),
      });
    } catch {
      return this.fail("offline");
    }

    if (!res.ok) {
      // The server answered and refused.
      return this.fail("rejected");
    }

    // A 200 the client cannot consume is a server-contract failure, not a
    // network one — `rejected`, never `offline`. `onTokenRefreshed` stays
    // inside this try so a throwing app callback resolves as a failure rather
    // than rejecting the in-flight promise.
    try {
      // Response shape validated by server OpenAPI contract; runtime validation
      // not needed for internal auth endpoint
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
        expires_at: string;
      };

      this.options.onTokenRefreshed(data.access_token, data.refresh_token, data.expires_at);
      return { outcome: "refreshed" };
    } catch {
      return this.fail("rejected");
    }
  }

  private fail(outcome: RefreshFailureOutcome): RefreshOutcome {
    this.options.onRefreshFailed(outcome);
    return { outcome };
  }
}

export { RefreshManager, type RefreshManagerOptions };
